import { selectIElementIdToColorMap } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMemo, type JSX } from "react";
import {
  Scatter,
  ScatterChart,
  ScatterShapeProps,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import { DebugOverlaySection } from "./debug-overlay-section";
import { useRenderingStatsContext } from "./rendering-stats-context";

const MAX_PARALLEL_TASKS = 20;

/** @returns visualization of the point cloud nodes which are being loaded. */
export function PointLoadingView(): JSX.Element | null {
  return (
    <DebugOverlaySection title="Point Cloud Node Loading">
      <LoadingVisualization />
    </DebugOverlaySection>
  );
}

function LoadingVisualization(): JSX.Element {
  const colorMap = useAppSelector(selectIElementIdToColorMap);
  const { pointCloudStatsMap } = useRenderingStatsContext();

  const loadingData = useMemo(
    () =>
      Object.entries(pointCloudStatsMap).map(([id, stats]) => ({
        loading: stats.loading.nodes,
        queued: stats.queued.nodes,
        color: colorMap[id],
      })),
    [pointCloudStatsMap, colorMap],
  );

  const maxQueued = useMemo(
    () =>
      Math.max(
        MAX_PARALLEL_TASKS,
        ...Object.values(pointCloudStatsMap).map((stats) => stats.queued.nodes),
      ),
    [pointCloudStatsMap],
  );

  const maxLoading = useMemo(
    () =>
      Math.max(
        MAX_PARALLEL_TASKS,
        ...Object.values(pointCloudStatsMap).map(
          (stats) => stats.loading.nodes,
        ),
      ),
    [pointCloudStatsMap],
  );

  return (
    <Stack sx={{ justifyContent: "center" }}>
      <ScatterChart
        width={300}
        height={150}
        data={loadingData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey="queued"
          type="number"
          label={{ value: "queued", style: { fill: grey[400] } }}
          stroke={grey[400]}
          domain={[0, maxQueued + 2]}
          ticks={[0, maxQueued]}
        />
        <YAxis
          dataKey="loading"
          type="number"
          label={{
            value: "loading",
            angle: -90,
            style: { fill: grey[400] },
          }}
          stroke={grey[400]}
          width={30}
          domain={[0, maxLoading + 2]}
          ticks={[0, maxLoading]}
        />
        <Scatter name="point loading" shape={ScatterPoint} />
        <Tooltip content={CustomTooltip} />
      </ScatterChart>
    </Stack>
  );
}

function ScatterPoint({
  cx,
  cy,
  payload,
}: Omit<ScatterShapeProps, "payload"> & {
  payload?: { color: string };
}): JSX.Element | null {
  if (cx === undefined || cy === undefined) return null;

  return (
    <circle cx={cx} cy={cy} r={2} fill={payload?.color} stroke={grey[900]} />
  );
}

function CustomTooltip({
  active,
  payload,
}: TooltipContentProps): JSX.Element | null {
  if (!active || payload.length === 0) {
    return null;
  }

  const { loading, queued } = payload[0].payload;

  return (
    <Box
      component="div"
      sx={{
        background: grey[1000],
        padding: 1,
        borderRadius: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          nodes loading
        </FaroText>
        <FaroText variant="bodyS" dark>
          {loading}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          nodes queued
        </FaroText>
        <FaroText variant="bodyS" dark>
          {queued}
        </FaroText>
      </Stack>
    </Box>
  );
}
